import { useCallback, useEffect, useState } from 'react';
import { IconDownload, IconMessage2Question, IconRefresh, IconTrash } from '@tabler/icons-react';
import Btn from './ui/Btn';
import SendExportToDriveBtn from './SendExportToDriveBtn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Onglet « Mémo d'interrogatoire » du dossier (ADR-024).
 *
 * Document interne avocat — questions par partie, contradictions à
 * exploiter, vigilance, moyens de procédure, chronologie critique.
 * Pas un document client.
 */

type MemoContent = {
  introduction?: string;
  parties?: Array<{
    partie_id?: string;
    nom_complet?: string;
    objectif_partie?: string;
    questions_clefs?: Array<{
      intitule?: string;
      pourquoi?: string;
      appui?: string;
    }>;
    contradictions_a_exploiter?: string[];
    vigilance?: string[];
  }>;
  moyens_procedure?: Array<{
    moyen?: string;
    argumentaire?: string;
    fondement?: string;
    appui?: string;
  }>;
  chronologie_critique?: Array<{
    date?: string;
    fait?: string;
    importance?: string;
  }>;
  questions_ouvertes?: string[];
};

type Memo = {
  id: string;
  dossier_id: string;
  title: string;
  content: MemoContent;
  audience_date: string | null;
  hint: string | null;
  model_used: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_eur: number | null;
  status: 'draft' | 'finalized' | 'archived';
  created_at: string;
  updated_at: string;
};

type Props = {
  dossierId: string;
};

export default function MemoInterrogatoireTab({ dossierId }: Props) {
  const [items, setItems] = useState<Memo[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ audience_date: '', hint: '' });

  const load = useCallback(async () => {
    try {
      const data = await api.get<Memo[]>(`/dossiers/${dossierId}/memos-interrogatoire`);
      setItems(data);
      if (!activeId && data.length > 0) setActiveId(data[0]!.id);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [dossierId, activeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const body: Record<string, string> = {};
      if (draft.audience_date) body.audience_date = draft.audience_date;
      if (draft.hint.trim()) body.hint = draft.hint.trim();
      const row = await api.post<Memo & { status?: string; async?: boolean }>(
        `/dossiers/${dossierId}/memos-interrogatoire/generate`,
        body
      );
      if ((row as { async?: boolean }).async || (row as { status?: string }).status === 'queued') {
        toast.success('Mémo lancé en arrière-plan (45s) — la liste se rechargera dans ~60s.');
        setShowForm(false);
        setDraft({ audience_date: '', hint: '' });
        window.setTimeout(() => void load(), 60_000);
      } else {
        toast.success('Mémo généré');
        setShowForm(false);
        setDraft({ audience_date: '', hint: '' });
        setActiveId(row.id);
        await load();
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Supprimer ce mémo ?')) return;
    try {
      await api.delete(`/memos-interrogatoire/${id}`);
      toast.success('Mémo supprimé');
      if (activeId === id) setActiveId(null);
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec');
    }
  };

  if (loading) {
    return <div className="px-6 py-5 text-[13px] text-text-secondary">Chargement…</div>;
  }

  const active = items.find((i) => i.id === activeId) ?? items[0];

  return (
    <div className="px-6 py-5 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">
            Mémo d'interrogatoire
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Document interne avocat — questions par partie, contradictions à exploiter, vigilance,
            moyens procédure. Consomme les items cochés dans la Synthèse IA.
          </p>
        </div>
        <Btn
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setShowForm((v) => !v)}
          disabled={generating}
        >
          <IconMessage2Question size={14} stroke={1.6} />
          {showForm ? 'Annuler' : 'Nouveau mémo'}
        </Btn>
      </header>

      {showForm && (
        <div className="rounded-md border border-rule bg-paper-elevated p-4 space-y-3">
          <label className="block text-[12px]">
            <span className="text-text-secondary">Date d'audience (optionnel)</span>
            <input
              type="date"
              value={draft.audience_date}
              onChange={(e) => setDraft((d) => ({ ...d, audience_date: e.target.value }))}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12.5px]"
            />
          </label>
          <label className="block text-[12px]">
            <span className="text-text-secondary">Priorités / point de vigilance (optionnel)</span>
            <textarea
              value={draft.hint}
              onChange={(e) => setDraft((d) => ({ ...d, hint: e.target.value }))}
              rows={3}
              placeholder="ex. Lever le mandat d'arrêt. Pousser sur Salduz. Le témoin Y a changé sa version."
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12.5px]"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Btn
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
              disabled={generating}
            >
              Annuler
            </Btn>
            <Btn
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void onGenerate()}
              disabled={generating}
            >
              <IconRefresh size={14} stroke={1.6} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Génération…' : 'Générer (Sonnet ~45s)'}
            </Btn>
          </div>
        </div>
      )}

      {items.length === 0 && !showForm && (
        <div className="rounded-md border border-dashed border-rule-strong bg-paper-elevated p-5 text-center text-[13px] text-text-tertiary">
          <IconMessage2Question size={32} stroke={1.4} className="mx-auto" />
          <p className="mt-2 font-medium text-text-primary">Aucun mémo généré</p>
          <p className="mt-1 text-[12px]">
            Lance d'abord l'analyse IA + coche les items pertinents dans la Synthèse IA, puis génère
            un mémo d'interrogatoire.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid gap-4 md:grid-cols-[260px_1fr]">
          <aside className="space-y-1.5">
            {items.map((m) => {
              const isActive = m.id === active?.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setActiveId(m.id)}
                  className={`w-full rounded-md border px-3 py-2 text-left text-[12.5px] transition ${
                    isActive
                      ? 'border-accent bg-accent-light'
                      : 'border-rule bg-paper-elevated hover:border-rule-strong'
                  }`}
                >
                  <div className="font-medium text-text-primary line-clamp-2">{m.title}</div>
                  <div className="mt-0.5 text-[11px] text-text-tertiary">
                    {new Date(m.created_at).toLocaleString('fr-BE', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </div>
                </button>
              );
            })}
          </aside>

          {active && (
            <MemoBody
              memo={active}
              dossierId={dossierId}
              onDelete={() => void onDelete(active.id)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function MemoBody({
  memo,
  dossierId,
  onDelete,
}: {
  memo: Memo;
  dossierId: string;
  onDelete: () => void;
}) {
  const c = memo.content || {};
  return (
    <article className="rounded-md border border-rule bg-paper-elevated p-5 space-y-5">
      <header className="flex items-start justify-between gap-2 flex-wrap">
        <h2 className="font-serif text-[16px] font-medium text-text-primary">{memo.title}</h2>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-text-tertiary">
            {memo.cost_eur !== null && <>{memo.cost_eur.toFixed(3)} € · </>}
            {memo.tokens_in?.toLocaleString('fr-BE')} in /{' '}
            {memo.tokens_out?.toLocaleString('fr-BE')} out
          </span>
          <Btn
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              void api
                .downloadBlob(
                  `/memos-interrogatoire/${memo.id}/export.docx`,
                  `${memo.title || 'memo'}.docx`
                )
                .catch((e) =>
                  toast.error(e instanceof ApiError ? e.message : 'Téléchargement échoué')
                );
            }}
            title="Télécharger en .docx (Word)"
          >
            <IconDownload size={14} stroke={1.6} /> Word
          </Btn>
          <SendExportToDriveBtn
            dossierId={dossierId}
            exportPath={`/memos-interrogatoire/${memo.id}/export.docx`}
            defaultFilename={`${memo.title || 'memo'}.docx`}
            defaultSubfolder="PROCEDURE"
            variant="icon"
          />
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-cat-def"
            title="Supprimer"
          >
            <IconTrash size={14} stroke={1.6} />
          </button>
        </div>
      </header>

      {c.introduction && (
        <section>
          <h3 className="text-[13.5px] font-semibold text-text-primary mb-1.5">Introduction</h3>
          <p className="text-[13px] leading-relaxed text-text-primary whitespace-pre-wrap">
            {c.introduction}
          </p>
        </section>
      )}

      {c.parties && c.parties.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-[13.5px] font-semibold text-text-primary">Stratégie par partie</h3>
          {c.parties.map((p, idx) => (
            <div key={idx} className="rounded-md border border-rule bg-paper-card p-3 space-y-2">
              <h4 className="text-[13px] font-medium text-text-primary">
                {p.nom_complet || `Partie ${idx + 1}`}
              </h4>
              {p.objectif_partie && (
                <p className="text-[12.5px] italic text-text-secondary">
                  Objectif : {p.objectif_partie}
                </p>
              )}
              {p.questions_clefs && p.questions_clefs.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[12px] font-medium text-text-secondary">Questions clés :</p>
                  <ol className="list-decimal pl-5 space-y-1.5 text-[12.5px]">
                    {p.questions_clefs.map((q, qi) => (
                      <li key={qi} className="text-text-primary">
                        <div className="font-medium">{q.intitule}</div>
                        {q.pourquoi && (
                          <div className="text-[11.5px] text-text-secondary mt-0.5">
                            → {q.pourquoi}
                          </div>
                        )}
                        {q.appui && (
                          <div className="text-[11px] font-mono text-text-tertiary mt-0.5">
                            ({q.appui})
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {p.contradictions_a_exploiter && p.contradictions_a_exploiter.length > 0 && (
                <div>
                  <p className="text-[12px] font-medium text-cat-def-pastel text-cat-def">
                    Contradictions à exploiter :
                  </p>
                  <ul className="list-disc pl-5 space-y-0.5 text-[12px] text-text-primary">
                    {p.contradictions_a_exploiter.map((co, ci) => (
                      <li key={ci}>{co}</li>
                    ))}
                  </ul>
                </div>
              )}
              {p.vigilance && p.vigilance.length > 0 && (
                <div>
                  <p className="text-[12px] font-medium text-cat-imp-text">Vigilance :</p>
                  <ul className="list-disc pl-5 space-y-0.5 text-[12px] text-text-secondary">
                    {p.vigilance.map((v, vi) => (
                      <li key={vi}>{v}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {c.moyens_procedure && c.moyens_procedure.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[13.5px] font-semibold text-text-primary">Moyens de procédure</h3>
          <ul className="space-y-2">
            {c.moyens_procedure.map((mp, mi) => (
              <li key={mi} className="rounded-md border border-rule bg-paper-card p-2.5">
                <div className="text-[12.5px] font-medium text-text-primary">{mp.moyen}</div>
                {mp.argumentaire && (
                  <div className="text-[12px] text-text-secondary mt-1">{mp.argumentaire}</div>
                )}
                {mp.fondement && (
                  <div className="text-[11.5px] font-mono text-text-tertiary mt-0.5">
                    {mp.fondement}
                  </div>
                )}
                {mp.appui && (
                  <div className="text-[11px] text-text-tertiary mt-0.5">({mp.appui})</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {c.chronologie_critique && c.chronologie_critique.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[13.5px] font-semibold text-text-primary">Chronologie critique</h3>
          <ul className="space-y-1.5 text-[12.5px]">
            {c.chronologie_critique.map((ch, chi) => (
              <li key={chi} className="text-text-primary">
                <span className="font-mono text-text-secondary">{ch.date}</span> — {ch.fait}
                {ch.importance && (
                  <div className="text-[11.5px] italic text-text-secondary">{ch.importance}</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {c.questions_ouvertes && c.questions_ouvertes.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="text-[13.5px] font-semibold text-text-primary">
            Questions ouvertes (à clarifier avant audience)
          </h3>
          <ul className="list-disc pl-5 space-y-1 text-[12.5px] text-text-primary">
            {c.questions_ouvertes.map((q, qi) => (
              <li key={qi}>{q}</li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
